import type { ExerciseType } from '@/api/exercises'

/**
 * Вычистка эталонов из содержимого — только для локального предпросмотра.
 *
 * Настоящая защита живёт на сервере (`ExerciseContentSanitizer`): клиенту
 * доверять нельзя, и эта функция нужна лишь чтобы учитель увидел задание таким,
 * каким его получит ученик. Список полей повторяет серверный — расходиться им
 * нельзя, иначе предпросмотр врёт.
 */
export function sanitizeForStudent(type: ExerciseType, content: Record<string, unknown>): Record<string, unknown> {
  const clone = JSON.parse(JSON.stringify(content ?? {})) as Record<string, unknown>

  switch (type) {
    case 'drag_gaps':
    case 'cloze':
      delete clone.gaps

      return clone

    case 'dropdown_gaps': {
      const gaps = Array.isArray(clone.gaps) ? (clone.gaps as Array<Record<string, unknown>>) : []
      clone.gaps = gaps.map((gap) => {
        const copy = { ...gap }
        delete copy.correct

        return copy
      })

      return clone
    }

    case 'choice':
    case 'picture': {
      const options = Array.isArray(clone.options) ? (clone.options as Array<Record<string, unknown>>) : []
      clone.options = options.map((option) => {
        const copy = { ...option }
        delete copy.correct

        return copy
      })

      const hotspots = Array.isArray(clone.hotspots) ? (clone.hotspots as Array<Record<string, unknown>>) : []
      clone.hotspots = hotspots.map((hotspot) => {
        const copy = { ...hotspot }
        delete copy.answers

        return copy
      })

      return clone
    }

    case 'true_false': {
      const statements = Array.isArray(clone.statements) ? (clone.statements as Array<Record<string, unknown>>) : []
      clone.statements = statements.map((statement) => {
        const copy = { ...statement }
        delete copy.answer

        return copy
      })

      return clone
    }

    case 'sentence_builder':
    case 'translation':
      delete clone.accepted

      return clone

    case 'ordering': {
      // Порядок хранения и есть ответ — перемешиваем, как это делает сервер
      const items = Array.isArray(clone.items)
        ? (clone.items as unknown[]).map((item) =>
            typeof item === 'string' ? item : String((item as { text?: string }).text ?? ''),
          )
        : []

      for (let i = items.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[items[i], items[j]] = [items[j], items[i]]
      }

      clone.items = items

      return clone
    }

    case 'matching': {
      // Правая колонка отделяется от пар: в паре она и была бы ответом
      const pairs = Array.isArray(clone.pairs) ? (clone.pairs as Array<Record<string, unknown>>) : []
      const options = pairs.map((pair) => String(pair.right ?? ''))

      for (let i = options.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[options[i], options[j]] = [options[j], options[i]]
      }

      clone.pairs = pairs.map((pair) => ({ left: pair.left }))
      clone.options = options

      return clone
    }

    case 'grouping': {
      const items = Array.isArray(clone.items) ? (clone.items as Array<Record<string, unknown>>) : []
      clone.items = items.map((item) => {
        const copy = { ...item }
        delete copy.group

        return copy
      })

      return clone
    }

    case 'error_correction':
      delete clone.error_index
      delete clone.corrections

      return clone

    case 'crossword': {
      const words = Array.isArray(clone.words) ? (clone.words as Array<Record<string, unknown>>) : []
      clone.words = words.map((word) => ({ clue: word.clue ?? '' }))

      // В филворде укладки убираются целиком: по координатам клеток слова
      // читаются прямо из сетки букв
      if (clone.variant === 'wordsearch') {
        const grid = (clone.grid ?? {}) as Record<string, unknown>
        delete grid.placements
        clone.grid = grid
      }

      return clone
    }

    case 'listening': {
      const audio = (clone.audio ?? {}) as Record<string, unknown>
      // Текст озвучки — это и есть ответ на аудирование
      if (audio.source === 'tts') delete audio.text
      clone.audio = audio

      // Вложенные вопросы чистятся своими правилами — как на сервере
      clone.questions = (Array.isArray(clone.questions) ? clone.questions : []).map((question) => {
        const item = question as { type?: ExerciseType; content?: Record<string, unknown> }

        return item.type === undefined
          ? item
          : { ...item, content: sanitizeForStudent(item.type, item.content ?? {}) }
      })

      return clone
    }

    case 'dictation': {
      // Белым списком, а не вычёркиванием `source_text` и `accepted_answers`:
      // весь смысл диктанта в том, что текст надо расслышать, и поле,
      // добавленное в контент завтра, при вычёркивании уехало бы в предпросмотр
      // вместе с эталоном. Список повторяет серверный `dictationForStudent`.
      const allowed = ['audio', 'max_plays', 'playback_rate']

      return Object.fromEntries(Object.entries(clone).filter(([key]) => allowed.includes(key)))
    }

    default:
      // Открытый и устный ответы и тест по карточкам эталонов не держат
      return clone
  }
}
